/**
 * Camada de API do módulo Prospect dentro do shell SAAS.
 *
 * Reusa o axios `api` principal do shell (com interceptor JWT do SaasUser
 * admin), prefixando todos os paths com `/prospect`. O backend
 * `services/api/modules/prospect/middleware/auth.ts` aceita JWT do
 * SaasUser admin pra rotas que exigem role 'admin' (ver Fase 14a).
 *
 * Diferenças vs apps/prospect-web/src/services/api.ts:
 *   - Sem `authService` — admin já está autenticado no shell.
 *   - Sem token storage local — usa o token global do shell SAAS.
 */
import api from '../../../services/api';
import type {
  ActiveUserLocation,
  AppNotification,
  Appointment,
  AppointmentPeriodo,
  Contract,
  ContractDocument,
  ContractGeoPoint,
  ContractStats,
  DueDate,
  FollowupData,
  Plan,
  Pop,
  SellerStats,
  SlotLimit,
  SlotsAvailability,
  User,
  Vendedor,
} from './types';

const PFX = '/prospect';

// ── Contracts ──────────────────────────────────────────────────────────

export const contractService = {
  async create(tipo: 'F' | 'J', payload: Record<string, unknown>): Promise<{ contract: Contract }> {
    const { data } = await api.post<{ contract: Contract }>(`${PFX}/contracts`, { tipo, ...payload });
    return data;
  },
  async update(contractId: string, tipo: 'F' | 'J', payload: Record<string, unknown>): Promise<{ contract: Contract }> {
    const { data } = await api.patch<{ contract: Contract }>(`${PFX}/contracts/${contractId}`, { tipo, ...payload });
    return data;
  },
  async list(): Promise<Contract[]> {
    const { data } = await api.get<Contract[]>(`${PFX}/contracts`);
    return data;
  },
  async get(id: string): Promise<Contract> {
    const { data } = await api.get<Contract>(`${PFX}/contracts/${id}`);
    return data;
  },
  async approve(id: string): Promise<{ contract: Contract }> {
    const { data } = await api.post<{ contract: Contract }>(`${PFX}/contracts/${id}/approve`);
    return data;
  },
  async reject(id: string, reason: string): Promise<{ contract: Contract }> {
    const { data } = await api.post<{ contract: Contract }>(`${PFX}/contracts/${id}/reject`, { reason });
    return data;
  },
  async myStats(): Promise<ContractStats> {
    const { data } = await api.get<ContractStats>(`${PFX}/contracts/stats/me`);
    return data;
  },
  /** Stats agregadas de TODO o time (todas as vendas) — Fase 25. */
  async teamStats(): Promise<ContractStats> {
    const { data } = await api.get<ContractStats>(`${PFX}/contracts/stats/team`);
    return data;
  },
  /** Follow-up mensal com gráficos YTD (Fase 25). month: 'YYYY-MM'. */
  async followup(month?: string): Promise<FollowupData> {
    const { data } = await api.get<FollowupData>(`${PFX}/contracts/followup`, {
      params: month ? { month } : {},
    });
    return data;
  },
  async decline(id: string, reason: string): Promise<{ contract: Contract }> {
    const { data } = await api.post<{ contract: Contract }>(`${PFX}/contracts/${id}/decline`, { reason });
    return data;
  },
  async sellersStats(): Promise<SellerStats[]> {
    const { data } = await api.get<SellerStats[]>(`${PFX}/contracts/stats/sellers`);
    return data;
  },
  async getGeo(filters?: {
    vendedorId?: number;
    dateFrom?: string;
    dateTo?: string;
    approvalStatuses?: string[];
  }): Promise<ContractGeoPoint[]> {
    const params = new URLSearchParams();
    if (filters?.vendedorId) params.set('vendedorId', String(filters.vendedorId));
    if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.set('dateTo', filters.dateTo);
    if (filters?.approvalStatuses?.length) params.set('approvalStatuses', filters.approvalStatuses.join(','));
    const { data } = await api.get<ContractGeoPoint[]>(`${PFX}/contracts/geo?${params.toString()}`);
    return data;
  },
  async listDocuments(contractId: string): Promise<ContractDocument[]> {
    const { data } = await api.get<ContractDocument[]>(`${PFX}/contracts/${contractId}/documents`);
    return data;
  },
  async getDocumentFull(contractId: string, docId: string): Promise<{ full: string; fileName: string; descricao: string }> {
    const { data } = await api.get<{ full: string; fileName: string; descricao: string }>(
      `${PFX}/contracts/${contractId}/documents/${docId}/full`,
    );
    return data;
  },
  async deleteDocument(contractId: string, docId: string): Promise<void> {
    await api.delete(`${PFX}/contracts/${contractId}/documents/${docId}`);
  },
  async uploadDocument(
    contractId: string,
    file: File,
    descricao: string,
    onProgress?: (pct: number) => void,
  ): Promise<{ document: ContractDocument }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('descricao', descricao);
    const { data } = await api.post<{ document: ContractDocument }>(
      `${PFX}/contracts/${contractId}/documents`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60_000,
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
      },
    );
    return data;
  },
  async exportXlsx(filters?: {
    vendedorId?: number;
    dateFrom?: string;
    dateTo?: string;
    approvalStatuses?: string[];
  }): Promise<void> {
    const params = new URLSearchParams();
    if (filters?.vendedorId) params.set('vendedorId', String(filters.vendedorId));
    if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.set('dateTo', filters.dateTo);
    if (filters?.approvalStatuses?.length) params.set('approvalStatuses', filters.approvalStatuses.join(','));
    const response = await api.get(`${PFX}/contracts/export?${params.toString()}`, {
      responseType: 'blob',
      timeout: 60_000,
    });
    const url = window.URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contratos-${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

// ── ERP Data ──────────────────────────────────────────────────────────

export const erpDataService = {
  async sync(): Promise<void> {
    await api.post(`${PFX}/erp/sync`);
  },
  async getPlans(): Promise<Plan[]> {
    const { data } = await api.get<Plan[]>(`${PFX}/erp/plans`);
    return data;
  },
  async getDueDates(): Promise<DueDate[]> {
    const { data } = await api.get<DueDate[]>(`${PFX}/erp/duedates`);
    return data;
  },
  async getPops(): Promise<Pop[]> {
    const { data } = await api.get<Pop[]>(`${PFX}/erp/pops`);
    return data;
  },
  async getVendedores(): Promise<Vendedor[]> {
    const { data } = await api.get<Vendedor[]>(`${PFX}/erp/vendedores`);
    return data;
  },
};

// ── Notifications ─────────────────────────────────────────────────────

export const notificationService = {
  async list(): Promise<AppNotification[]> {
    const { data } = await api.get<AppNotification[]>(`${PFX}/notifications`);
    return data;
  },
  async unreadCount(): Promise<number> {
    const { data } = await api.get<{ count: number }>(`${PFX}/notifications/unread-count`);
    return data.count;
  },
  async markRead(id: string): Promise<void> {
    await api.post(`${PFX}/notifications/${id}/read`);
  },
  async markAllRead(): Promise<void> {
    await api.post(`${PFX}/notifications/read-all`);
  },
};

// ── Admin ─────────────────────────────────────────────────────────────

export const adminService = {
  async listUsers(): Promise<User[]> {
    const { data } = await api.get<User[]>(`${PFX}/admin/users`);
    return data;
  },
  async createUser(payload: Record<string, unknown>): Promise<User> {
    const { data } = await api.post<User>(`${PFX}/admin/users`, payload);
    return data;
  },
  async updateUser(id: string, payload: Record<string, unknown>): Promise<User> {
    const { data } = await api.patch<User>(`${PFX}/admin/users/${id}`, payload);
    return data;
  },
  async deleteUser(id: string): Promise<void> {
    await api.delete(`${PFX}/admin/users/${id}`);
  },
  async upsertSecret(userId: string, payload: { erpToken: string; erpApp: string }): Promise<void> {
    await api.put(`${PFX}/admin/users/${userId}/secret`, payload);
  },
  async getSecret(userId: string): Promise<{ erpApp: string; description?: string } | null> {
    try {
      const { data } = await api.get(`${PFX}/admin/users/${userId}/secret`);
      return data;
    } catch {
      return null;
    }
  },
};

// ── Appointments / Slot Limits ────────────────────────────────────────

export const appointmentService = {
  async listSlotLimits(): Promise<SlotLimit[]> {
    const { data } = await api.get<SlotLimit[]>(`${PFX}/appointments/slot-limits`);
    return data;
  },
  async upsertSlotLimit(cidade: string, payload: { manha: number; tarde: number }): Promise<SlotLimit> {
    const { data } = await api.put<SlotLimit>(
      `${PFX}/appointments/slot-limits/${encodeURIComponent(cidade)}`,
      payload,
    );
    return data;
  },
  async getByContract(contractId: string): Promise<Appointment | null> {
    try {
      const { data } = await api.get<Appointment>(`${PFX}/appointments/by-contract/${contractId}`);
      return data;
    } catch (e) {
      const err = e as { response?: { status?: number } };
      if (err.response?.status === 404) return null;
      throw e;
    }
  },
  async getAvailability(cidade: string): Promise<SlotsAvailability> {
    const { data } = await api.get<SlotsAvailability>(`${PFX}/appointments/availability`, {
      params: { cidade },
    });
    return data;
  },
  async create(payload: {
    contractId: string;
    data: string;
    periodo: AppointmentPeriodo;
    /** true = pedido de encaixe (vaga extra) — vai pra aprovação */
    isSqueeze?: boolean;
  }): Promise<{ appointment: Appointment }> {
    const { data } = await api.post<{ appointment: Appointment }>(`${PFX}/appointments`, payload);
    return data;
  },
  async reschedule(id: string, payload: { data: string; periodo: AppointmentPeriodo }): Promise<{ appointment: Appointment }> {
    const { data } = await api.patch<{ appointment: Appointment }>(`${PFX}/appointments/${id}`, payload);
    return data;
  },
  /** Aprova encaixe pendente (cria chamado no ERP + notifica o vendedor). */
  async approveSqueeze(id: string): Promise<{ appointment: Appointment }> {
    const { data } = await api.post<{ appointment: Appointment }>(
      `${PFX}/appointments/${id}/approve-squeeze`,
    );
    return data;
  },
  /** Reprova encaixe pendente (restaura agendamento anterior + notifica). */
  async rejectSqueeze(id: string, reason: string): Promise<{ appointment: Appointment }> {
    const { data } = await api.post<{ appointment: Appointment }>(
      `${PFX}/appointments/${id}/reject-squeeze`,
      { reason },
    );
    return data;
  },
};

// ── User Location ─────────────────────────────────────────────────────

export const userLocationService = {
  async reportMine(payload: { lat: number; lng: number; accuracy?: number }): Promise<void> {
    await api.post(`${PFX}/user-locations/me`, payload);
  },
  async listActive(): Promise<ActiveUserLocation[]> {
    const { data } = await api.get<ActiveUserLocation[]>(`${PFX}/user-locations/active`);
    return data;
  },
};
